<script setup lang="ts">
const { t: $t } = useI18n()

import type { CreateOrEditLogisticsDefectTaskPopupConfig } from '~/components/Logistics/DefectTask/CreateOrEditContainer.vue'

const props = defineProps<{
  defectId: string
  class?: string
  viewOnly?: boolean
}>()

const { logisticsTask: queryLogisticsTask } = useQuery()
const { data: logisticsTasks, isLoading: isLoadingLogisticsTasks } = queryLogisticsTask.byDefectId(props.defectId)

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()
const deleteLogisticsTask = useMutation({
  mutationFn: $trpc.logisticsTask.delete.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('logisticsTask.notification.delete.error') }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['logisticsTask'] })
    popupConfirmLogisticsIdToDelete.value = null
    notification.success({ title: $t('logisticsTask.notification.delete.success'), duration: 4500 })
  },
})

const createOrEditConfig = ref<CreateOrEditLogisticsDefectTaskPopupConfig | null>(null)
const popupConfirmLogisticsIdToDelete = ref<null | string>(null)
</script>

<template>
  <div :class="props.class" class="space-y-4">
    <div class="flex justify-between items-center">
      <h2 class="font-bold">
        {{ $t('defect.logisticsTask.list.title', { count: logisticsTasks ? logisticsTasks.length : 0 }) }}
      </h2>
      <n-button v-if="!viewOnly" @click="() => createOrEditConfig = { mode: 'create', defectId: props.defectId }">
        {{ $t('defect.logisticsTask.create.title') }}
      </n-button>
    </div>
    <div v-if="isLoadingLogisticsTasks">
      <n-skeleton height="30vh" :sharp="false" />
    </div>
    <p v-else-if="!logisticsTasks || logisticsTasks.length === 0">
      {{ $t('defect.logisticsTask.list.empty') }}
    </p>
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 items-start">
      <LogisticsPlanningDefectTaskBreadcrumb
        v-for="logisticsTask in logisticsTasks"
        :key="logisticsTask.id"
        :task="logisticsTask"
        :is-clicked="ref(false)"
        :show-action-buttons="!viewOnly"
        @update="createOrEditConfig = { mode: 'update', id: logisticsTask.id }"
        @delete="popupConfirmLogisticsIdToDelete = logisticsTask.id"
      />
    </div>
  </div>

  <LogisticsDefectTaskCreateOrEditContainer
    v-if="createOrEditConfig"
    :config="createOrEditConfig"
    @close="createOrEditConfig = null"
  />

  <TheConfirmPopup
    v-if="popupConfirmLogisticsIdToDelete"
    @confirm="deleteLogisticsTask.mutate({ id: popupConfirmLogisticsIdToDelete })"
    @close="popupConfirmLogisticsIdToDelete = null"
  >
    {{ $t('defect.logisticsTask.delete.confirm', { id: popupConfirmLogisticsIdToDelete }) }}
  </TheConfirmPopup>
</template>
